import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Container,
  Paper,
  TextField,
  Typography,
  Grid,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DescriptionIcon from '@mui/icons-material/Description';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { styled, useTheme } from '@mui/material/styles';
import { motion } from 'framer-motion';
import axios from 'axios';
import OverallScoreBanner from './OverallScoreBanner';
import InlineAnalysisBadge from './InlineAnalysisBadge';
import { getFieldAnalyses } from './analysisFieldMapper';
import { PREFECTURES, parseLocation, buildLocation } from '../../constants/prefectures';
import { bg, text as textColors, shadow, gradient, border, overlay } from '../../utils/darkModeAdapter';
import { saveCommonFields, loadCommonFields } from '../../utils/commonProfileStore';

const API_BASE_URL = process.env.REACT_APP_API_URL;

const FormPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(5),
  borderRadius: theme.spacing(3),
  background: gradient(theme).subtle,
  boxShadow: shadow(theme).lg,
  border: `1px solid ${border(theme).light}`,
  position: 'relative',
  overflow: 'hidden',
}));

const FormTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  marginBottom: theme.spacing(4),
  textAlign: 'center',
  color: '#249BE0',
  fontSize: '1.8rem',
  letterSpacing: '-0.02em',
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: theme.spacing(1.5),
    transition: 'all 0.3s ease',
    backgroundColor: bg(theme).card,
    '&:hover': {
      transform: 'translateY(-1px)',
      boxShadow: shadow(theme).sm,
    },
    '&.Mui-focused': {
      transform: 'translateY(-1px)',
      boxShadow: '0 8px 24px rgba(25,118,210,0.15)',
    }
  },
  '& .MuiInputLabel-root': {
    fontWeight: 600,
    color: textColors(theme).secondary,
    '&.Mui-focused': {
      color: theme.palette.primary.main,
    }
  },
  '& .MuiFormHelperText-root': {
    marginTop: theme.spacing(1),
    fontSize: '0.875rem',
    color: textColors(theme).muted,
  }
}));

const SubmitButton = styled(Button)(({ theme }) => ({
  minWidth: 220,
  padding: theme.spacing(1.8, 4),
  borderRadius: theme.spacing(2),
  fontSize: '1.1rem',
  fontWeight: 700,
  letterSpacing: '0.02em',
  background: 'linear-gradient(135deg, #1976d2 0%, #2196f3 50%, #42a5f5 100%)',
  backgroundSize: '200% 200%',
  boxShadow: '0 8px 24px rgba(25,118,210,0.3)',
  transition: 'all 0.4s ease',
  textTransform: 'none',
  '&:hover': {
    backgroundPosition: 'right center',
    transform: 'translateY(-2px)',
    boxShadow: '0 12px 32px rgba(25,118,210,0.4)',
  },
  '&:active': {
    transform: 'translateY(0px)',
  },
  '&:disabled': {
    background: theme.palette.action.disabledBackground,
    color: theme.palette.action.disabled,
    boxShadow: 'none',
    transform: 'none',
  }
}));

/** 初期入力フォーム画面 */
function InitialFormScreen({ creationMode, defaultValues, onSubmit, isLoading, onBack }) {
  const theme = useTheme();
  // creationModeに基づいてデフォルト値を設定
  const getInitialFormData = () => {
    const saved = loadCommonFields();
    if (creationMode === 'new') {
      return defaultValues || {
        role: saved.position || '',
        skills: '',
        salary: saved.salary || '',
        location: saved.location || '',
        targetPerson: '',
        companyPr: '',
      };
    } else if (creationMode === 'existing') {
      return defaultValues || {
        title: '',
        summary: '',
        jobDescription: '',
        targetPerson: '',
      };
    }
    return {};
  };

  const [formData, setFormData] = useState(getInitialFormData());
  const [validationErrors, setValidationErrors] = useState({});

  // PDF関連のstate
  const [pdfUploading, setPdfUploading] = useState(false);
  const [pdfError, setPdfError] = useState(null);
  const [pdfFileName, setPdfFileName] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  // 評価関連のstate
  const [analysis, setAnalysis] = useState(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const analysisTimerRef = useRef(null);

  // PDFファイル処理の共通関数
  const processPdfFile = async (file) => {
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.pdf')) {
      setPdfError('PDFファイルのみアップロード可能です');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setPdfError('ファイルサイズが大きすぎます（最大10MB）');
      return;
    }

    setPdfError(null);
    setPdfUploading(true);

    try {
      const uploadData = new FormData();
      uploadData.append('file', file);

      const response = await axios.post(
        `${API_BASE_URL}/job_posting/extract_pdf`,
        uploadData,
        {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' },
        }
      );

      if (response.data.status === 'success') {
        setFormData(prev => ({
          ...prev,
          title: response.data.title || prev.title,
          summary: response.data.summary || prev.summary,
          jobDescription: response.data.jobDescription || prev.jobDescription,
          targetPerson: response.data.targetPerson || prev.targetPerson,
        }));
        setPdfFileName(file.name);
        setValidationErrors({});
      }
    } catch (err) {
      console.error('PDF extraction error:', err);
      setPdfError(
        err.response?.data?.detail ||
        'PDFの解析に失敗しました。手動で入力してください。'
      );
    } finally {
      setPdfUploading(false);
    }
  };

  // PDFアップロードハンドラー（ボタンクリック）
  const handlePdfUpload = async (event) => {
    const file = event.target.files[0];
    await processPdfFile(file);
  };

  // ドラッグ&ドロップハンドラー
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  }, []);

  const handleDrop = useCallback(async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);

    const files = e.dataTransfer?.files;
    if (files && files.length > 0) {
      await processPdfFile(files[0]);
    }
  }, []);

  // 評価の自動実行（existingモードのみ、デバウンス1秒）
  useEffect(() => {
    if (creationMode !== 'existing') return;

    const { title, summary, jobDescription, targetPerson } = formData;
    const allFilled = title?.trim() && summary?.trim() && jobDescription?.trim() && targetPerson?.trim();

    if (!allFilled) {
      setAnalysis(null);
      return;
    }

    if (analysisTimerRef.current) {
      clearTimeout(analysisTimerRef.current);
    }

    analysisTimerRef.current = setTimeout(async () => {
      setAnalysisLoading(true);
      try {
        const content = `# ${title}\n\n## 概要\n${summary}\n\n## 仕事内容\n${jobDescription}\n\n## 求める人材\n${targetPerson}`;
        const response = await axios.post(
          `${API_BASE_URL}/job_posting/analyze`,
          { content, title },
          { withCredentials: true }
        );
        if (response.data?.analysis) {
          setAnalysis(response.data.analysis);
        }
      } catch (err) {
        console.error('Analysis error:', err);
        setAnalysis(null);
      } finally {
        setAnalysisLoading(false);
      }
    }, 1000);

    return () => {
      if (analysisTimerRef.current) {
        clearTimeout(analysisTimerRef.current);
      }
    };
  }, [formData, creationMode]);

  // creationModeに応じてバリデーションを変更
  const validateForm = useCallback(() => {
    const errors = {};
    let requiredFields = [];

    if (creationMode === 'new') {
      requiredFields = ['role', 'skills', 'salary', 'location'];
    } else if (creationMode === 'existing') {
      requiredFields = ['title', 'summary', 'jobDescription', 'targetPerson'];
    }

    requiredFields.forEach(field => {
      const value = formData[field];
      if (!value || (typeof value === 'string' && !value.trim())) {
        errors[field] = '必須項目です';
      }
    });

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData, creationMode]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;

    // 給与は数字のみ許可（新規作成モードの場合のみ）
    if (name === 'salary' && creationMode === 'new') {
      newValue = newValue.replace(/\D/g, '');
    }

    setFormData(prev => ({
      ...prev,
      [name]: newValue
    }));

    // 入力があればエラーを消す
    if (value.trim() && validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (validateForm()) {
      const submitData = { ...formData };

      if (creationMode === 'new' && formData.salary) {
        submitData.salary = parseInt(formData.salary);
      }

      // existingモードで評価の改善ポイントがあれば含める
      if (creationMode === 'existing' && analysis?.improvements?.length > 0) {
        submitData.improvements = analysis.improvements;
      }

      // 共通プロフィールに保存
      if (creationMode === 'new') {
        saveCommonFields({
          position: formData.role,
          salary: formData.salary,
          location: formData.location,
        });
      }

      onSubmit(submitData);
    }
  };

  // 必須項目のスタイル
  const getRequiredFieldStyle = (value) => {
    const isDark = theme.palette.mode === 'dark';
    return {
      backgroundColor: !value || (typeof value === 'string' && value.trim() === '')
        ? (isDark ? 'rgba(255,200,200,0.06)' : 'rgba(255,248,248,0.8)')
        : bg(theme).card,
      borderRadius: '12px',
    };
  };

  // 給与の表示形式を変換（例：500000 → 50万円）
  const formatSalary = (value) => {
    if (!value) return '';
    const num = parseInt(value);
    return isNaN(num) ? '' : `${Math.floor(num / 10000)}万円`;
  };

  // フォームの有効性をチェック（creationModeに応じて）
  const isFormValid = () => {
    let requiredFields = [];

    if (creationMode === 'new') {
      requiredFields = ['role', 'skills', 'salary', 'location'];
    } else if (creationMode === 'existing') {
      requiredFields = ['title', 'summary', 'jobDescription', 'targetPerson'];
    }

    return Object.keys(validationErrors).length === 0 &&
      requiredFields.every(field => {
        const value = formData[field];
        return value && (typeof value === 'string' ? value.trim() : true);
      });
  };

  // フォームタイトルの表示
  const getFormTitle = () => {
    if (creationMode === 'new') {
      return '新しい求人票の作成';
    } else if (creationMode === 'existing') {
      return '過去の求人票をもとに作成';
    }
    return '求人情報の入力';
  };

  // 送信ボタンのテキスト
  const getSubmitButtonText = () => {
    if (isLoading) {
      return '生成中...';
    }

    if (creationMode === 'new') {
      return '求人票を生成する';
    } else if (creationMode === 'existing') {
      return '求人票を生成する';
    }

    return '求人票を生成する';
  };

  return (
    <Container maxWidth="md" sx={{ py: 5 }}>
      <FormPaper
        component={motion.div}
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
      >
        <Box component="form" onSubmit={handleSubmit} noValidate>
          <Box sx={{ position: 'relative', mb: 3 }}>
            {onBack && (
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={onBack}
                size="small"
                sx={{ position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)', color: 'text.secondary', minWidth: 'auto', whiteSpace: 'nowrap' }}
              >
                戻る
              </Button>
            )}
            <FormTitle variant="h4" sx={{ mb: 0, textAlign: 'center' }}>
              {getFormTitle()}
            </FormTitle>
          </Box>

          {Object.values(validationErrors).some(error => error) && (
            <Alert
              severity="error"
              sx={{
                mb: 4,
                borderRadius: 2,
                '& .MuiAlert-message': {
                  fontWeight: 600
                }
              }}
            >
              必須項目を入力してください
            </Alert>
          )}

          <Grid container spacing={3}>
            {/* 新規作成モードのフォーム */}
            {creationMode === 'new' && (
              <>
                <Grid item xs={12}>
                  <StyledTextField
                    required
                    fullWidth
                    id="role"
                    name="role"
                    label="募集職種"
                    placeholder="例: Webアプリケーションエンジニア"
                    value={formData.role || ''}
                    onChange={handleChange}
                    variant="outlined"
                    error={!!validationErrors.role}
                    helperText={validationErrors.role || '募集する職種名・ポジション名を具体的に入力してください。例：フルスタックエンジニア、法人営業、カスタマーサポートなど'}
                    InputProps={{
                      style: getRequiredFieldStyle(formData.role)
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <StyledTextField
                    required
                    fullWidth
                    id="skills"
                    name="skills"
                    label="必要なスキル・経験"
                    placeholder="例: React, TypeScript, Webアプリケーション開発経験3年以上"
                    value={formData.skills || ''}
                    onChange={handleChange}
                    variant="outlined"
                    multiline
                    rows={3}
                    error={!!validationErrors.skills}
                    helperText={validationErrors.skills || '必須スキル、歓迎スキル、必要な経験年数などを記載してください。例：JavaScript 3年以上、AWS経験、プロジェクトマネジメント経験など（カンマ区切り）'}
                    InputProps={{
                      style: getRequiredFieldStyle(formData.skills)
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <StyledTextField
                    required
                    fullWidth
                    id="salary"
                    name="salary"
                    label="給与"
                    placeholder="例: 500000"
                    value={formData.salary || ''}
                    onChange={handleChange}
                    variant="outlined"
                    error={!!validationErrors.salary}
                    helperText={validationErrors.salary || `月給を円単位で入力してください（半角数字のみ）。例：月給25万円の場合「250000」。現在の入力: ${formatSalary(formData.salary)}`}
                    InputProps={{
                      style: getRequiredFieldStyle(formData.salary)
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={3}>
                  <FormControl
                    fullWidth
                    required
                    variant="outlined"
                    error={!!validationErrors.location}
                  >
                    <InputLabel>都道府県</InputLabel>
                    <Select
                      label="都道府県"
                      value={parseLocation(formData.location).prefecture}
                      onChange={(e) => {
                        const { detail } = parseLocation(formData.location);
                        setFormData(prev => ({
                          ...prev,
                          location: buildLocation(e.target.value, detail)
                        }));
                        if (validationErrors.location) {
                          setValidationErrors(prev => ({ ...prev, location: '' }));
                        }
                      }}
                      sx={{
                        borderRadius: 2,
                        ...(formData.location ? {} : { backgroundColor: 'rgba(255, 243, 224, 0.3)' })
                      }}
                    >
                      <MenuItem value="">選択してください</MenuItem>
                      {PREFECTURES.map((pref) => (
                        <MenuItem key={pref} value={pref}>{pref}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <StyledTextField
                    fullWidth
                    id="locationDetail"
                    name="locationDetail"
                    label="補足（市区町村など）"
                    placeholder="例: 渋谷区（リモート可）"
                    value={parseLocation(formData.location).detail}
                    onChange={(e) => {
                      const { prefecture } = parseLocation(formData.location);
                      setFormData(prev => ({
                        ...prev,
                        location: buildLocation(prefecture, e.target.value)
                      }));
                    }}
                    variant="outlined"
                    helperText="市区町村、リモート可否など"
                  />
                </Grid>

                <Grid item xs={12}>
                  <StyledTextField
                    fullWidth
                    id="targetPerson"
                    name="targetPerson"
                    label="求める人材"
                    placeholder="例: チームでの協力を大切にできる方、新しい技術に興味がある方"
                    value={formData.targetPerson || ''}
                    onChange={handleChange}
                    variant="outlined"
                    multiline
                    rows={3}
                    helperText="求める人物像、性格、価値観、ソフトスキルなどを記載してください。例：コミュニケーション能力が高い方、自発的に学習できる方、顧客目線で考えられる方など"
                  />
                </Grid>

                <Grid item xs={12}>
                  <StyledTextField
                    fullWidth
                    id="companyPr"
                    name="companyPr"
                    label="簡単なPR"
                    placeholder="例: 当社は2015年設立のスタートアップで、教育分野のDXに取り組んでいます"
                    value={formData.companyPr || ''}
                    onChange={handleChange}
                    variant="outlined"
                    multiline
                    rows={3}
                    helperText="会社の強み、事業内容、社風、福利厚生、成長機会など、応募者にアピールしたいポイントを記載してください。例：年間休日120日、フレックスタイム制、資格取得支援制度ありなど"
                  />
                </Grid>
              </>
            )}

            {/* 過去の求人票をもとに作成するモードのフォーム */}
            {creationMode === 'existing' && (
              <>
                {/* PDFアップロードセクション（ドラッグ&ドロップ対応） */}
                <Grid item xs={12}>
                  <Box
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    sx={{
                      border: '2px dashed',
                      borderColor: pdfError ? 'error.main' : dragOver ? theme.palette.primary.main : border(theme).default,
                      borderRadius: 3,
                      p: 4,
                      textAlign: 'center',
                      backgroundColor: dragOver ? 'rgba(25, 118, 210, 0.08)' : overlay(theme).lightest,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        borderColor: pdfUploading ? border(theme).default : theme.palette.primary.main,
                        backgroundColor: pdfUploading ? overlay(theme).lightest : 'rgba(25, 118, 210, 0.04)',
                      },
                      mb: 1,
                    }}
                  >
                    {pdfUploading ? (
                      <Box>
                        <CircularProgress size={40} sx={{ mb: 2 }} />
                        <Typography variant="body1" color="text.secondary">
                          PDFを解析中...内容を抽出しています
                        </Typography>
                      </Box>
                    ) : pdfFileName ? (
                      <Box>
                        <CheckCircleIcon sx={{ fontSize: 48, color: '#4CAF50', mb: 1 }} />
                        <Typography variant="h6" sx={{ mb: 0.5, color: '#4CAF50', fontWeight: 600 }}>
                          PDF読み込み完了
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
                          <DescriptionIcon sx={{ fontSize: 18, color: textColors(theme).muted }} />
                          <Typography variant="body2" color="text.secondary">
                            {pdfFileName}
                          </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          下のフォームに内容が反映されました。必要に応じて編集してください。
                        </Typography>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<DeleteIcon />}
                          onClick={() => {
                            setPdfFileName(null);
                            setFormData({ title: '', summary: '', jobDescription: '', targetPerson: '' });
                            setAnalysis(null);
                          }}
                          sx={{ borderRadius: 20, px: 2, color: textColors(theme).disabled, borderColor: border(theme).default }}
                        >
                          クリア
                        </Button>
                      </Box>
                    ) : (
                      <Box>
                        <CloudUploadIcon sx={{ fontSize: 48, color: dragOver ? theme.palette.primary.main : textColors(theme).disabled, mb: 2 }} />
                        <Typography variant="h6" sx={{ mb: 1, color: dragOver ? theme.palette.primary.main : textColors(theme).secondary }}>
                          {dragOver ? 'ここにドロップしてアップロード' : '過去の求人票PDFをアップロード'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          ドラッグ&ドロップ、またはボタンからPDFを選択できます
                        </Typography>
                        <Button
                          variant="outlined"
                          component="label"
                          startIcon={<CloudUploadIcon />}
                          sx={{ borderRadius: 20, px: 3 }}
                        >
                          PDFを選択
                          <input
                            type="file"
                            accept=".pdf"
                            hidden
                            onChange={handlePdfUpload}
                          />
                        </Button>
                      </Box>
                    )}
                  </Box>
                  {pdfError && (
                    <Alert severity="error" sx={{ mt: 1, borderRadius: 2 }}>
                      {pdfError}
                    </Alert>
                  )}
                </Grid>

                {/* 総合スコアバナー */}
                {(analysis || analysisLoading) && (
                  <Grid item xs={12}>
                    <OverallScoreBanner analysis={analysis} isLoading={analysisLoading} />
                  </Grid>
                )}


                <Grid item xs={12}>
                  <Typography variant="body1" sx={{ textAlign: 'center', color: 'text.secondary', my: 1 }}>
                    または、手動で入力して生成
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <StyledTextField
                    required
                    fullWidth
                    id="title"
                    name="title"
                    label="タイトル"
                    placeholder="例: Webアプリケーションエンジニア募集"
                    value={formData.title || ''}
                    onChange={handleChange}
                    variant="outlined"
                    error={!!validationErrors.title}
                    helperText={validationErrors.title || '求人票のタイトルを入力してください。応募者の目を引く、具体的で魅力的なタイトルが効果的です。例：「リモートOK」「未経験歓迎」などのキーワードを含めると良いでしょう'}
                    InputProps={{
                      style: getRequiredFieldStyle(formData.title)
                    }}
                  />
                  {analysis && <InlineAnalysisBadge analyses={getFieldAnalyses(analysis, 'title')} />}
                </Grid>

                <Grid item xs={12}>
                  <StyledTextField
                    required
                    fullWidth
                    id="summary"
                    name="summary"
                    label="概要"
                    placeholder="例: 当社のプロダクト開発チームでWebアプリケーションの設計・開発・運用に携わるエンジニアを募集しています"
                    value={formData.summary || ''}
                    onChange={handleChange}
                    variant="outlined"
                    multiline
                    rows={3}
                    error={!!validationErrors.summary}
                    helperText={validationErrors.summary || '募集背景、ポジションの重要性、チームの状況などを簡潔に記載してください。例：事業拡大に伴い新規プロジェクトのリードエンジニアを募集、既存メンバーと連携してサービス開発を推進など'}
                    InputProps={{
                      style: getRequiredFieldStyle(formData.summary)
                    }}
                  />
                  {analysis && <InlineAnalysisBadge analyses={getFieldAnalyses(analysis, 'summary')} />}
                </Grid>

                <Grid item xs={12}>
                  <StyledTextField
                    required
                    fullWidth
                    id="jobDescription"
                    name="jobDescription"
                    label="仕事内容"
                    placeholder="例: Webアプリケーションの設計・開発・保守、APIの開発、技術選定など"
                    value={formData.jobDescription || ''}
                    onChange={handleChange}
                    variant="outlined"
                    multiline
                    rows={4}
                    error={!!validationErrors.jobDescription}
                    helperText={validationErrors.jobDescription || '日々の具体的な業務内容、使用するツール・技術、チーム構成、プロジェクト例などを詳細に記載してください。応募者が入社後の働き方をイメージできるように書くことが重要です'}
                    InputProps={{
                      style: getRequiredFieldStyle(formData.jobDescription)
                    }}
                  />
                  {analysis && <InlineAnalysisBadge analyses={getFieldAnalyses(analysis, 'jobDescription')} />}
                </Grid>

                <Grid item xs={12}>
                  <StyledTextField
                    required
                    fullWidth
                    id="targetPerson"
                    name="targetPerson"
                    label="求める人材"
                    placeholder="例: Web開発経験3年以上、チーム開発の経験がある方、主体的に課題解決できる方"
                    value={formData.targetPerson || ''}
                    onChange={handleChange}
                    variant="outlined"
                    multiline
                    rows={3}
                    error={!!validationErrors.targetPerson}
                    helperText={validationErrors.targetPerson || '必須要件、歓迎要件、求める人物像を分けて記載してください。例：【必須】Web開発3年以上、【歓迎】アジャイル開発経験、【人物像】新しい技術に興味がある方など'}
                    InputProps={{
                      style: getRequiredFieldStyle(formData.targetPerson)
                    }}
                  />
                  {analysis && <InlineAnalysisBadge analyses={getFieldAnalyses(analysis, 'targetPerson')} />}
                </Grid>
              </>
            )}

            {/* フィールド横断の分析（給与透明性・選考プロセス）- ボタンの上に表示 */}
            {creationMode === 'existing' && analysis && (
              <Grid item xs={12}>
                <Box sx={{ mt: 1 }}>
                  <InlineAnalysisBadge analyses={getFieldAnalyses(analysis, 'overall')} />
                  {analysis?.improvements?.length > 0 && (
                    <Alert severity="info" sx={{ mt: 1, borderRadius: 2 }}>
                      上記の改善ポイントは「求人票を生成する」ボタン押下時にAIが自動的に反映します。
                    </Alert>
                  )}
                </Box>
              </Grid>
            )}

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <SubmitButton
                  type="submit"
                  variant="contained"
                  disabled={isLoading || !isFormValid()}
                  component={motion.button}
                  whileHover={{ scale: isLoading || !isFormValid() ? 1 : 1.02 }}
                  whileTap={{ scale: isLoading || !isFormValid() ? 1 : 0.98 }}
                >
                  {isLoading ? (
                    <>
                      <CircularProgress size={24} color="inherit" sx={{ mr: 2 }} />
                      生成中...
                    </>
                  ) : (
                    getSubmitButtonText()
                  )}
                </SubmitButton>
              </Box>
            </Grid>

          </Grid>
        </Box>
      </FormPaper>
    </Container>
  );
}

export default InitialFormScreen;
